import React from "react";
import { motion } from "framer-motion";
import { BoxIcon } from "lucide-react";
interface FloatingStatusCardProps {
  icon: BoxIcon;
  /** Large figure, where the card is a count rather than a statement. */
  value?: string;
  label: string;
  meta?: string;
  tone?: 'brand' | 'info' | 'warn' | 'violet';
  /** Position within the hero composition. */
  className?: string;
  /** Stagger index, so the cards settle in sequence rather than together. */
  delay?: number;
}
const TONE = {
  brand: 'bg-brand-50 text-brand-700',
  info: 'bg-info-50 text-info-700',
  warn: 'bg-warn-50 text-warn-700',
  violet: 'bg-[#efe9fb] text-[#6d28d9]'
};

/**
 * A fragment of real Campuzon UI lifted out of the dashboard and floated over
 * the hero. Each one corresponds to something the product actually shows, so
 * they read as the interface rather than as decoration.
 */
export function FloatingStatusCard({
  icon: Icon,
  value,
  label,
  meta,
  tone = 'brand',
  className,
  delay = 0
}: FloatingStatusCardProps) {
  return <motion.div aria-hidden initial={{
    opacity: 0,
    y: 12,
    scale: 0.96
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.28,
    ease: [0.23, 1, 0.32, 1],
    delay
  }} className={`pointer-events-none absolute flex items-center gap-2.5 rounded-xl border border-line bg-surface px-3 py-2.5 shadow-[0_12px_32px_rgba(15,23,42,0.12)] ${className ?? ''}`} data-id="element-2315" data-mp="2tkql6v3oxsva">
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${TONE[tone]}`} data-id="element-2316" data-mp="2awd6w2edveog">
        <Icon aria-hidden className="h-4 w-4" data-id="element-2317" data-mp="2fun56lvemvea" />
      </span>
      <span className="min-w-0" data-id="element-2318" data-mp="2ksoon7wqswqa">
        {value && <span className="cz-num block text-body font-bold leading-5 text-ink" data-id="element-2319" data-mp="2krner6rgqga5">{value}</span>}
        <span className="block whitespace-nowrap text-caption font-semibold text-ink" data-id="element-2320" data-mp="2plhs7sgh5z0s">{label}</span>
        {meta && <span className="block whitespace-nowrap text-caption text-ink-subtle" data-id="element-2321" data-mp="1s4aoali6lobk">{meta}</span>}
      </span>
    </motion.div>;
}